/**
 * SalesDashboard — Sales Manager view.
 *
 * Based on `SalesDashboard` from aura-dash-1.jsx (lines 387–551).
 * Widgets: KPI strip (5) + overdue alert + Pipeline Kanban + Rep quota + Loss reasons.
 *
 * UI-only. Kanban drag-drop DEFERRED (read-only view in this pass).
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, Users, Plus, Sparkles } from 'lucide-react';
import { formatINRCompact, splitINRCompact, formatNum, safeGet } from './_dashboardShared';

// ─── Types ──────────────────────────────────────────────────────────────

interface KpiPayload {
  value?: number | string | null;
  delta?: number;
  trend?: Array<{ t: string; v: number }>;
  breakdown?: Array<{ label: string; value: number }>;
  meta?: Record<string, unknown>;
}

interface Stage {
  id: string;
  name: string;
  color?: string;
  order?: number;
}

interface Deal {
  id: string;
  name?: string;
  value?: number;
  probability?: number;
  stageId?: string;
  stage?: Stage;
  account?: { name?: string };
  owner?: { firstName?: string; lastName?: string; name?: string };
  expectedCloseDate?: string;
  source?: string;
}

// ─── Atoms ──────────────────────────────────────────────────────────────

function Progress({ value, color = 'var(--forest-600)', h = 4 }: { value: number; color?: string; h?: number }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  return (
    <div style={{ height: h, background: 'var(--ivory-3)', borderRadius: h }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: h, transition: 'width .3s' }} />
    </div>
  );
}

function Avatar({ name, size = 22 }: { name: string; size?: number }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join('')
    .toUpperCase();
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: 'var(--ivory-3)',
        color: 'var(--ink-1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.4,
        fontWeight: 500,
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
}

function Skeleton({ h = 80 }: { h?: number }) {
  return <div style={{ height: h, background: 'var(--ivory-2)', borderRadius: 6, animation: 'pulse 1.5s ease-in-out infinite' }} />;
}

function NoData({ label = 'No data yet' }: { label?: string }) {
  return <div style={{ fontSize: 12, color: 'var(--ink-3)', padding: '8px 0' }}>{label}</div>;
}

function EmptyChip() {
  return <span className="chip" style={{ fontSize: 11 }}>Unavailable</span>;
}

// ─── KPI strip card ────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  subtitle,
  delta,
  up,
  loading,
}: {
  label: string;
  value: string;
  subtitle?: string;
  delta?: string;
  up?: boolean;
  loading?: boolean;
}) {
  return (
    <div className="card" style={{ padding: 14 }}>
      <div className="kpi-label" style={{ fontSize: 10 }}>{label}</div>
      {loading ? <Skeleton h={40} /> : (
        <>
          <div className="num-display" style={{ fontSize: 22, marginTop: 2 }}>{value}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
            {delta && <span style={{ color: up ? 'var(--forest-600)' : 'var(--danger)' }}>{delta}</span>}
            {subtitle && <span style={{ color: 'var(--ink-3)' }}>{subtitle}</span>}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Kanban ────────────────────────────────────────────────────────────

function PipelineKanban() {
  const [stages, setStages] = useState<Stage[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      safeGet<{ items?: Stage[]; stages?: Stage[] }>('/stages'),
      safeGet<{ items?: Deal[]; deals?: Deal[] }>('/deals?limit=50'),
    ]).then(([s, d]) => {
      const stageList = (s.data as { items?: Stage[]; stages?: Stage[] } | Stage[] | null);
      const dealList = (d.data as { items?: Deal[]; deals?: Deal[] } | Deal[] | null);
      const parsedStages: Stage[] = Array.isArray(stageList) ? stageList : (stageList?.items ?? stageList?.stages ?? []);
      const parsedDeals: Deal[] = Array.isArray(dealList) ? dealList : (dealList?.items ?? dealList?.deals ?? []);
      setStages(parsedStages);
      setDeals(parsedDeals);
      setError(s.error || d.error);
      setLoading(false);
    });
  }, []);

  if (loading) return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} h={380} />)}</div>;
  if (error && stages.length === 0) return <div className="card" style={{ padding: 24 }}><NoData label="Pipeline unavailable" /></div>;
  if (stages.length === 0) return <div className="card" style={{ padding: 24 }}><NoData label="No pipeline stages configured yet" /></div>;

  const byStage = stages.map((s) => ({
    ...s,
    deals: deals.filter((d) => (d.stageId ?? d.stage?.id) === s.id),
  }));

  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(stages.length, 6)}, 1fr)`, gap: 12, marginBottom: 20 }}>
      {byStage.slice(0, 6).map((s) => {
        const total = s.deals.reduce((a, d) => a + (d.value ?? 0), 0);
        return (
          <div
            key={s.id}
            style={{
              background: 'var(--ivory-1)',
              borderRadius: 10,
              border: '1px solid var(--line, var(--ivory-3))',
              minHeight: 380,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--ivory-3)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 6, height: 6, borderRadius: 3, background: s.color ?? 'var(--saffron)' }} />
              <span style={{ fontSize: 12, fontWeight: 500 }}>{s.name}</span>
              <span className="num" style={{ fontSize: 11, color: 'var(--ink-3)', background: 'var(--ivory-2)', padding: '1px 6px', borderRadius: 8 }}>{s.deals.length}</span>
              <div style={{ flex: 1 }} />
              <span className="num-display" style={{ fontSize: 12 }}>{formatINRCompact(total)}</span>
            </div>
            <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto', flex: 1 }}>
              {s.deals.slice(0, 6).map((d) => {
                const ownerName = d.owner?.name
                  ?? [d.owner?.firstName, d.owner?.lastName].filter(Boolean).join(' ')
                  ?? '—';
                return (
                  <div
                    key={d.id}
                    style={{
                      background: 'var(--ivory-0)',
                      borderRadius: 6,
                      border: '1px solid var(--ivory-3)',
                      padding: 10,
                      boxShadow: 'var(--sh-1, 0 1px 2px rgba(0,0,0,.04))',
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink-0)', lineHeight: 1.3, marginBottom: 4 }}>{d.name ?? 'Unnamed'}</div>
                    <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 8 }}>{d.account?.name ?? ''}</div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div className="num-display" style={{ fontSize: 14 }}>{formatINRCompact(d.value ?? 0)}</div>
                      {d.source && <span className="chip" style={{ fontSize: 10, height: 16 }}>{d.source}</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Avatar name={ownerName} size={18} />
                      <span style={{ fontSize: 10, color: 'var(--ink-3)', flex: 1 }}>
                        {d.expectedCloseDate ? new Date(d.expectedCloseDate).toLocaleDateString('en-IN') : ''}
                      </span>
                      {d.probability != null && <span className="num" style={{ fontSize: 10, color: 'var(--ink-3)' }}>{d.probability}%</span>}
                    </div>
                  </div>
                );
              })}
              {s.deals.length === 0 && (
                <div style={{ fontSize: 11, color: 'var(--ink-4)', padding: '12px 4px', textAlign: 'center' }}>No deals</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Rep quota attainment ────────────────────────────────────────────────

function RepQuotaCard() {
  const [data, setData] = useState<KpiPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    safeGet<KpiPayload>('/analytics/sales/quota-attainment').then((r) => {
      setData(r.data);
      setError(r.error);
      setLoading(false);
    });
  }, []);

  const rows = (data?.breakdown ?? []).slice(0, 6) as Array<{ label: string; value: number; meta?: { quota?: number } }>;

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="kpi-label" style={{ marginBottom: 12 }}>Rep quota attainment</div>
      {loading ? <Skeleton h={200} /> : error && rows.length === 0 ? <EmptyChip /> : rows.length === 0 ? <NoData /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map((r, i) => {
            const quota = r.meta?.quota ?? 0;
            const achieved = r.value;
            const pct = quota > 0 ? (achieved / quota) * 100 : 0;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Avatar name={r.label} size={22} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', fontSize: 12, marginBottom: 3 }}>
                    <span style={{ flex: 1, fontWeight: 500 }}>{r.label}</span>
                    <span className="num" style={{ color: 'var(--ink-3)' }}>
                      {formatINRCompact(achieved)}{quota ? ` / ${formatINRCompact(quota)}` : ''}
                    </span>
                  </div>
                  <Progress value={pct} color={pct >= 100 ? 'var(--forest)' : pct >= 75 ? 'var(--saffron)' : '#C67C1B'} h={4} />
                </div>
                <span
                  className="num"
                  style={{
                    width: 40,
                    textAlign: 'right',
                    fontWeight: 500,
                    fontSize: 12,
                    color: pct >= 100 ? 'var(--forest-600)' : 'var(--ink-1)',
                  }}
                >
                  {Math.round(pct)}%
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Loss reasons chart ──────────────────────────────────────────────────

function LossReasonsCard() {
  const [data, setData] = useState<KpiPayload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    safeGet<KpiPayload>('/analytics/sales/loss-reasons').then((r) => {
      setData(r.data);
      setLoading(false);
    });
  }, []);

  const reasons = ((data?.breakdown ?? []) as Array<{ label: string; value: number }>).slice(0, 6);
  const max = reasons.reduce((m, r) => Math.max(m, r.value), 0) || 1;
  const palette = ['var(--danger)', '#C67C1B', 'var(--saffron)', 'var(--ink-3)', 'var(--info)', '#A89A7E'];

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="kpi-label" style={{ marginBottom: 12 }}>Lost reason breakdown</div>
      {loading ? <Skeleton h={180} /> : reasons.length === 0 ? <NoData /> : (
        <>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, height: 140, marginBottom: 12 }}>
            {reasons.map((b, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <div style={{ flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end' }}>
                  <div style={{ width: '100%', height: `${(b.value / max) * 100}%`, background: palette[i % palette.length], borderRadius: '2px 2px 0 0' }} />
                </div>
                <div style={{ fontSize: 10, color: 'var(--ink-3)', textAlign: 'center' }}>{b.label}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--saffron-50)', borderRadius: 6, fontSize: 12 }}>
            <Sparkles size={13} color="var(--saffron-600)" />
            <span style={{ flex: 1 }}>Drill into {reasons[0]?.label ?? 'top reason'} deals to find common themes.</span>
            {/* Apr 27 (audit P1) — Analyze opens AI panel with loss-reason analysis preset. */}
            <button
              className="btn btn-sm"
              onClick={() => window.dispatchEvent(new CustomEvent('aura-ai-open', { detail: { preset: 'sales_loss_analysis' } }))}
              style={{ fontSize: 11, padding: '3px 8px' }}
            >
              Analyze · 8 cr
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── At-risk deals row ───────────────────────────────────────────────────

function AtRiskStrip() {
  const [data, setData] = useState<KpiPayload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    safeGet<KpiPayload>('/analytics/sales/overdue-followups').then((r) => {
      setData(r.data);
      setLoading(false);
    });
  }, []);

  if (loading) return <Skeleton h={40} />;

  const count = typeof data?.value === 'number' ? data.value : 0;
  if (count === 0) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        marginBottom: 16,
        background: 'var(--danger-bg)',
        borderRadius: 8,
        border: '1px solid #EFCFCA',
        fontSize: 13,
        color: 'var(--danger)',
      }}
    >
      <div style={{ width: 6, height: 6, borderRadius: 3, background: 'var(--danger)' }} />
      <strong>{count} follow-up{count === 1 ? '' : 's'} overdue</strong>
      <div style={{ flex: 1 }} />
      {/* Apr 27 (audit P1) — Review navigates to Activities filtered by overdue. */}
      <a
        href="/activities?filter=overdue-followups"
        className="btn btn-sm"
        style={{
          background: '#fff',
          border: '1px solid #EFCFCA',
          fontSize: 12,
          padding: '3px 10px',
          textDecoration: 'none',
          color: 'var(--danger)',
        }}
      >
        Review
      </a>
    </div>
  );
}

// ─── Main ────────────────────────────────────────────────────────────────

export function SalesDashboard() {
  const navigate = useNavigate();
  // KPI strip data
  const [pipelineVal, setPipelineVal] = useState<KpiPayload | null>(null);
  const [forecast, setForecast] = useState<KpiPayload | null>(null);
  const [conversion, setConversion] = useState<KpiPayload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      safeGet<KpiPayload>('/analytics/sales/revenue-forecast'),
      safeGet<KpiPayload>('/analytics/sales/stage-conversion'),
      safeGet<KpiPayload>('/analytics/owner-dashboard'),
    ]).then(([fc, cv, od]) => {
      setForecast(fc.data);
      setConversion(cv.data);
      // Pull pipeline from owner aggregate
      const ownerData = od.data as { pipeline?: { total?: number; count?: number } } | null;
      setPipelineVal({
        value: ownerData?.pipeline?.total ?? null,
        meta: { count: ownerData?.pipeline?.count ?? 0 },
      });
      setLoading(false);
    });
  }, []);

  const pipelineTotal = typeof pipelineVal?.value === 'number' ? pipelineVal.value : null;
  const dealCount = (pipelineVal?.meta?.count as number) ?? 0;
  const pipeCompact = splitINRCompact(pipelineTotal);

  const weightedTotal = typeof forecast?.value === 'number' ? forecast.value : null;
  const weightedCompact = splitINRCompact(weightedTotal);

  const winRate = typeof conversion?.value === 'number' ? conversion.value : null;

  return (
    <div className="aura-root" style={{ padding: 24, background: 'var(--ivory-0)', minHeight: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--ff-serif)', fontSize: 22, fontWeight: 500, color: 'var(--ink-0)' }}>Sales pipeline</h2>
          <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
        </div>
        <div style={{ flex: 1 }} />
        <button
          className="btn btn-outline"
          onClick={() => navigate('/deals?filter=open')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Filter size={13} /> Filter
        </button>
        <button
          className="btn btn-outline"
          onClick={() => navigate('/deals')}
          style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Users size={13} /> All reps
        </button>
        {/* Apr 27 (audit P0) — wired "New deal" to /deals where the Add Deal modal lives. */}
        <button
          className="btn"
          onClick={() => navigate('/deals?new=1')}
          style={{
            marginLeft: 8,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: 'var(--ink-0)',
            color: 'var(--ivory-0)',
            border: '1px solid var(--ink-0)',
            padding: '8px 14px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          <Plus size={13} /> New deal
        </button>
      </div>

      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 20 }}>
        <KpiCard
          label="Pipeline value"
          value={pipelineTotal != null ? `${pipeCompact.num} ${pipeCompact.unit}` : '—'}
          subtitle={`${formatNum(dealCount)} deals`}
          loading={loading}
        />
        <KpiCard
          label="Weighted forecast"
          value={weightedTotal != null ? `${weightedCompact.num} ${weightedCompact.unit}` : '—'}
          subtitle="Probability-weighted"
          loading={loading}
        />
        <KpiCard
          label="Stage conversion"
          value={winRate != null ? `${Math.round(winRate)}%` : '—'}
          subtitle="Lead → Won"
          loading={loading}
        />
        <KpiCard
          label="Avg deal size"
          value={pipelineTotal && dealCount ? formatINRCompact(pipelineTotal / dealCount) : '—'}
          subtitle="Open deals"
          loading={loading}
        />
        <KpiCard
          label="Deals open"
          value={formatNum(dealCount)}
          subtitle="In pipeline"
          loading={loading}
        />
      </div>

      {/* At-risk strip */}
      <AtRiskStrip />

      {/* Kanban */}
      <PipelineKanban />

      {/* Secondary row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <RepQuotaCard />
        <LossReasonsCard />
      </div>
    </div>
  );
}

export default SalesDashboard;
